<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  
  const dispatch = createEventDispatcher<{ win: string }>();
  
  interface Prize {
    label: string;
    color: string;
    value: string;
  }
  
  // Green-themed prizes for Grn Haus Games
  const prizes: Prize[] = [
    { label: '1 Play', color: '#2ecc71', value: '1 Game Play' },
    { label: 'Ludo', color: '#27ae60', value: 'Unlock Ludo' },
    { label: '3 Plays', color: '#1abc9c', value: '3 Game Plays' },
    { label: 'BJack', color: '#16a085', value: 'Unlock Blackjack' },
    { label: '5 Plays', color: '#2ecc71', value: '5 Game Plays' },
    { label: '10Play', color: '#27ae60', value: '10 Game Plays' },
    { label: 'Chess+', color: '#1abc9c', value: 'Chess Pro Mode' },
    { label: 'Bonus', color: '#16a085', value: '15 Game Plays' },
  ];
  
  let isSpinning = $state(false);
  let rotation = $state(0);
  let winner = $state<string | null>(null);
  
  const segmentAngle = 360 / prizes.length;
  
  function spin() {
    if (isSpinning) return;
    
    isSpinning = true;
    winner = null;
    
    const extraRotations = (5 + Math.random() * 5) * 360;
    const randomSegment = Math.floor(Math.random() * prizes.length);
    const targetRotation = rotation + extraRotations + (randomSegment * segmentAngle);
    
    rotation = targetRotation;
    
    setTimeout(() => {
      const normalizedRotation = rotation % 360;
      const winningIndex = Math.floor((360 - normalizedRotation) / segmentAngle) % prizes.length;
      winner = prizes[winningIndex].value;
      
      dispatch('win', winner);
      isSpinning = false;
      
      // Play win sound
      playWinSound();
    }, 4000);
  }
  
  function playWinSound() {
    // Create a simple win sound using Web Audio API
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 523.25; // C5
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (e) {
      console.log('Audio not supported');
    }
  }
</script>

<div class="wheel-wrapper">
  <div class="wheel-container">
    <div class="pointer"></div>
    
    <div 
      class="wheel" 
      style="transform: rotate({rotation}deg); transition: transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99);"
    >
      {#each prizes as prize, index}
        <div 
          class="segment"
          style="background: {prize.color}; transform: rotate({index * segmentAngle}deg) skewY(-{90 - segmentAngle}deg);"
        >
          <span 
            class="segment-label"
            style="transform: skewY({90 - segmentAngle}deg) rotate({segmentAngle / 2}deg);"
          >
            {prize.label}
          </span>
        </div>
      {/each}
    </div>
    
    <div class="center-circle">
      <span class="spin-text">{isSpinning ? '...' : 'SPIN'}</span>
    </div>
    
    {#if !isSpinning}
      <button class="spin-button" onclick={spin}>
        🎰 SPIN!
      </button>
    {/if}
  </div>
  
  {#if winner && !isSpinning}
    <div class="winner-announcement">
      <h3>🎉 CONGRATULATIONS! 🎉</h3>
      <p>You won: <strong>{winner}</strong></p>
      <p class="green-message">♻️ Keep playing green!</p>
    </div>
  {/if}
</div>

<style>
  .wheel-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
  }
  
  .wheel-container {
    position: relative;
    width: 350px;
    height: 350px;
    border-radius: 50%;
    box-shadow: 0 0 30px rgba(46, 204, 113, 0.4), 0 0 60px rgba(39, 174, 96, 0.3);
    overflow: hidden;
    border: 8px solid #2ecc71;
  }
  
  .wheel {
    width: 100%;
    height: 100%;
    position: relative;
    border-radius: 50%;
  }
  
  .segment {
    position: absolute;
    width: 50%;
    height: 50%;
    top: 50%;
    left: 50%;
    transform-origin: 0% 0%;
    display: flex;
    align-items: center;
    justify-content: center;
    clip-path: polygon(0 100%, 100% 0, 100% 100%);
  }
  
  .segment-label {
    position: absolute;
    right: 30px;
    top: 50%;
    transform: translateY(-50%);
    font-weight: 800;
    font-size: 1rem;
    color: white;
    text-shadow: 1px 1px 2px rgba(0,0,0,0.5);
    white-space: nowrap;
  }
  
  .pointer {
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 20px solid transparent;
    border-right: 20px solid transparent;
    border-top: 40px solid #e74c3c;
    z-index: 10;
    filter: drop-shadow(0 4px 6px rgba(0,0,0,0.3));
  }
  
  .center-circle {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, #2ecc71, #27ae60);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 4px solid white;
    box-shadow: 0 0 20px rgba(46, 204, 113, 0.5);
    z-index: 5;
  }
  
  .spin-text {
    color: white;
    font-weight: 800;
    font-size: 1rem;
    letter-spacing: 1px;
  }
  
  .spin-button {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: linear-gradient(135deg, #2ecc71, #27ae60);
    color: white;
    border: none;
    padding: 15px 30px;
    font-size: 1.2rem;
    font-weight: 700;
    border-radius: 50px;
    cursor: pointer;
    z-index: 20;
    transition: all 0.3s ease;
    box-shadow: 0 5px 20px rgba(46, 204, 113, 0.4);
  }
  
  .spin-button:hover {
    transform: translate(-50%, -50%) scale(1.1);
    box-shadow: 0 8px 30px rgba(46, 204, 113, 0.6);
  }
  
  .winner-announcement {
    margin-top: 30px;
    background: linear-gradient(135deg, #2ecc71, #27ae60);
    color: white;
    padding: 25px 40px;
    border-radius: 20px;
    text-align: center;
    animation: bounce 0.5s ease infinite alternate;
    box-shadow: 0 10px 30px rgba(46, 204, 113, 0.4);
  }
  
  @keyframes bounce {
    from { transform: translateY(0); }
    to { transform: translateY(-10px); }
  }
  
  .winner-announcement h3 {
    font-size: 1.8rem;
    margin-bottom: 10px;
  }
  
  .winner-announcement p {
    font-size: 1.3rem;
  }
  
  .winner-announcement strong {
    font-size: 1.8rem;
    color: #fff;
    text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
  }
  
  .green-message {
    margin-top: 10px;
    font-size: 1rem !important;
    opacity: 0.9;
  }
  
  @media (max-width: 400px) {
    .wheel-container {
      width: 280px;
      height: 280px;
    }
    
    .segment-label {
      font-size: 0.85rem;
      right: 20px;
    }
  }
</style>
